'use client';

import { createContext, useContext, useState, useCallback } from 'react';

type SelectionState = {
  active: boolean;
  target: number;           // 0 = unlimited, >0 = cap
  selected: string[];       // room slugs the user has tapped
  availableIds: Set<number>; // Strapi CMS room IDs available for the dates
  checkin: string;
  checkout: string;
  adults: number;
  children: number;
};

const INITIAL: SelectionState = {
  active: false,
  target: 0,
  selected: [],
  availableIds: new Set(),
  checkin: '',
  checkout: '',
  adults: 1,
  children: 0,
};

type EnterOpts = {
  target: number;
  availableIds: number[];
  preSelected?: string[]; // slugs to pre-tick (rooms already chosen in widget)
  checkin: string;
  checkout: string;
  adults: number;
  children: number;
};

type RoomSelectionCtxType = {
  state: SelectionState;
  enter: (opts: EnterOpts) => void;
  toggle: (slug: string) => void;
  clear: () => void;
};

const RoomSelectionCtx = createContext<RoomSelectionCtxType | null>(null);

export function RoomSelectionProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<SelectionState>(INITIAL);

  const enter = useCallback((opts: EnterOpts) => {
    setState({
      active: true,
      target: opts.target,
      selected: opts.preSelected ?? [],
      availableIds: new Set(opts.availableIds),
      checkin: opts.checkin,
      checkout: opts.checkout,
      adults: opts.adults,
      children: opts.children,
    });
  }, []);

  const toggle = useCallback((slug: string) => {
    setState(prev => {
      const has = prev.selected.includes(slug);
      if (has) return { ...prev, selected: prev.selected.filter(s => s !== slug) };
      if (prev.target > 0 && prev.selected.length >= prev.target) return prev;
      return { ...prev, selected: [...prev.selected, slug] };
    });
  }, []);

  const clear = useCallback(() => setState(INITIAL), []);

  return (
    <RoomSelectionCtx.Provider value={{ state, enter, toggle, clear }}>
      {children}
    </RoomSelectionCtx.Provider>
  );
}

/* Returns null when used outside a provider — safe on pages without selection mode */
export function useRoomSelection() {
  return useContext(RoomSelectionCtx);
}
